import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from "./components/ui/toaster";
import PrivateRoute from './components/auth/PrivateRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import SendPrayer from './components/SendPrayer';
import ReceivePrayer from './components/ReceivePrayer';
import AdminDashboard from './components/AdminDashboard';
import { Loader2, LogOut } from 'lucide-react';
import { Button } from './components/ui/button';
import { useAuth } from './context/AuthContext';


const MainMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#171720] text-white p-6">
      <div className="max-w-md mx-auto">
        <div className="flex justify-between items-start mb-12">
          <div className="text-center flex-1">
            <h1 className="text-4xl font-bold mb-2">
              Prayer Bridge
            </h1>
            <p className="text-gray-400">
              Connect through prayer, support with faith
            </p>
          </div>
          {!user?.isAnonymous && (
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#2D2D39] hover:bg-[#2D2D39] text-white ml-4"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          )}
        </div>
        <div className="space-y-4">
          <button 
            onClick={() => navigate('/send')} 
            className="w-full p-6 rounded-lg bg-[#23232F] hover:bg-[#2D2D39] 
                       transition-colors border border-[#2D2D39] text-left"
          >
            <h2 className="text-xl font-semibold mb-2">Send Prayer</h2>
            <p className="text-gray-400">Share your prayer request with others</p>
          </button>
          <button 
            onClick={() => navigate('/receive')} 
            className="w-full p-6 rounded-lg bg-[#23232F] hover:bg-[#2D2D39] 
                       transition-colors border border-[#2D2D39] text-left"
          >
            <h2 className="text-xl font-semibold mb-2">Receive Prayer</h2>
            <p className="text-gray-400">Support others through prayer</p>
          </button>
        </div>
      </div>
    </div>
  );
};

const AuthSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  React.useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      localStorage.setItem('prayerbridge_token', token);
      navigate('/');
    } else {
      navigate('/login');
    }
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#171720] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FF8A4C]" />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/success" element={<AuthSuccess />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <MainMenu />
              </PrivateRoute>
            }
          />
          <Route
            path="/send"
            element={
              <PrivateRoute>
                <SendPrayer />
              </PrivateRoute>
            }
          />
          <Route
            path="/receive"
            element={
              <PrivateRoute>
                <ReceivePrayer />
              </PrivateRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <PrivateRoute requiredRole="admin">
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          
          {/* Catch all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </Router>
    </AuthProvider>
  );
};

export default App;